import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ComingSoonBanner } from './components/layout/ComingSoonBanner';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import Home from './pages/Home';
import LegalPage from './pages/LegalPage';
import Contact from './pages/Contact';

function App() {
  const lastUpdated = "19th June 2026";
  const contactEmail = "contact@sussexendocrineclinic.co.uk";

  return (
    <Router>
      <div className="min-h-screen bg-paper font-sans flex flex-col">
        <ComingSoonBanner />
        <Navbar />
        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            
            <Route path="/privacy" element={
              <LegalPage title="Privacy Policy" lastUpdated={lastUpdated}>
                <p>Curve ("we", "us", "the app") is a companion app for people using GLP-1 weight-management medications. We built Curve to be private by design. This policy explains what we do — and don't — collect.</p>
                
                <h3>The short version</h3>
                <p>Curve stores your health information only on your device. We do not have servers that collect your medication, weight, dose, calorie, or side-effect data. We cannot see it, and we never sell it.</p>
                
                <h3>Information stored on your device</h3>
                <p>The following stays local to your phone and is never transmitted to us:</p>
                <ul>
                  <li>Medication name, dose, and reminder schedule</li>
                  <li>Weight entries and goal weight</li>
                  <li>Calorie goals and logs</li>
                  <li>Side-effect notes</li>
                  <li>App settings (language, units)</li>
                </ul>
                <p>This data is removed when you delete the app or use the in-app Reset option.</p>
                
                <h3>Information we do not collect</h3>
                <p>We do not collect names, email addresses, account credentials, contacts, precise location, or advertising identifiers. Curve has no user accounts.</p>
                
                <h3>Third-party services</h3>
                <p>Curve is distributed through the Apple App Store and Google Play. Those platforms may collect data under their own privacy policies. We do not control that data.</p>
                
                <h3>Contact</h3>
                <p>Questions? Email us at <a href={`mailto:${contactEmail}`} className="text-sky underline">{contactEmail}</a>.</p>
              </LegalPage>
            } />

            <Route path="/terms" element={
              <LegalPage title="Terms of Use" lastUpdated={lastUpdated}>
                <p>By downloading or using Curve, you agree to these terms. If you do not agree, please do not use the app.</p>
                
                <h3>1. What Curve is</h3>
                <p>Curve is a personal tracking and reminder tool for people using GLP-1 medications. It helps you log doses, weight, calories, and side effects, and set medication reminders.</p>
                
                <h3>2. Not a medical device or medical advice</h3>
                <p>Curve is not a medical device and does not provide medical advice, diagnosis, or treatment. It does not replace your doctor, pharmacist, or other healthcare provider. Always follow the guidance of a qualified professional regarding your medication and health.</p>
                
                <h3>3. Your responsibilities</h3>
                <ul>
                  <li>You are responsible for the accuracy of the information you enter.</li>
                  <li>Reminders are a convenience feature and may be delayed or missed. Do not rely on Curve as your sole method for taking medication.</li>
                  <li>You are responsible for keeping your device secure, since your data is stored locally.</li>
                </ul>

                <h3>4. Limitation of liability</h3>
                <p>To the maximum extent permitted by law, we are not liable for any health outcomes arising from your use of (or inability to use) Curve.</p>
                
                <h3>Contact</h3>
                <p><a href={`mailto:${contactEmail}`} className="text-sky underline">{contactEmail}</a></p>
              </LegalPage>
            } />

            <Route path="/disclaimer" element={
              <LegalPage title="Medical Disclaimer" lastUpdated={lastUpdated}>
                <p className="font-medium">Please read this carefully.</p>
                <p>Curve is a self-tracking and reminder tool. It is not a medical device, and it does not provide medical advice, diagnosis, treatment, or dosing recommendations.</p>
                
                <ul>
                  <li><strong>Consult your provider:</strong> Always consult your healthcare provider before starting, stopping, or changing any medication, dose, diet, or exercise plan.</li>
                  <li><strong>Emergencies:</strong> Do not use Curve for emergencies. If you think you are having a medical emergency, call your local emergency number immediately.</li>
                  <li><strong>Reminders:</strong> Reminders are not guaranteed. Notifications depend on your device and may be delayed or missed.</li>
                  <li><strong>Data Interpretation:</strong> Information you log is for your own reference. Curve does not interpret your data or alert you to medical risks.</li>
                </ul>
                
                <p>By using Curve, you acknowledge that you understand these limitations and that you use the app at your own discretion and risk.</p>
              </LegalPage>
            } />
          </Routes>
        </div>
        <Footer />
      </div>
    </Router>
  );
}

export default App;